import { useEffect, useState } from "react";
import {
  channelLabel,
  formatBuiltAtUtc,
  type ReleaseBuildInfo,
  type ReleaseChannel,
} from "@structura/core/release-metadata";
import { ReleaseEnvironmentBadge } from "./ReleaseEnvironmentBadge";
import { cn } from "@/lib/utils";
import { getBuildReleaseInfo } from "@/lib/release-runtime";

type ReleaseApiPayload = {
  build: ReleaseBuildInfo;
  pointers?: { prod: string | null; latestRc: string | null };
};

export function ReleaseInfoFooter({
  productLabel,
  compact = false,
  className,
}: {
  productLabel: string;
  compact?: boolean;
  className?: string;
}) {
  const [info, setInfo] = useState<ReleaseBuildInfo>(() => getBuildReleaseInfo());
  const [pointers, setPointers] = useState<{ prod: string | null; latestRc: string | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/release-info")
      .then((r) => (r.ok ? r.json() : null))
      .then((d: ReleaseApiPayload | null) => {
        if (cancelled || !d?.build) return;
        setInfo(d.build);
        if (d.pointers) setPointers(d.pointers);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const channel = info.channel as ReleaseChannel;
  const version = info.releaseLabel ?? info.version;

  if (compact) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <ReleaseEnvironmentBadge channel={channel} />
        <span className="truncate text-[10px] text-muted-foreground tabular-nums">{version}</span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-lg border border-border/60 bg-muted/20 px-2.5 py-2 text-[10px] leading-relaxed text-muted-foreground",
        className,
      )}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="font-medium text-foreground/80">{productLabel}</span>
        <ReleaseEnvironmentBadge channel={channel} />
      </div>
      <dl className="space-y-0.5">
        <Row label="Release" value={version} />
        <Row label="Commit" value={info.commit ?? "—"} mono />
        <Row label="Built" value={formatBuiltAtUtc(info.builtAt)} />
        <Row label="Branch" value={info.branch ?? "—"} mono />
        <Row label="Channel" value={channelLabel(channel)} />
        {pointers && (
          <>
            <Row label="Prod ref" value={pointers.prod ?? "—"} />
            <Row label="Latest RC" value={pointers.latestRc ?? "—"} />
          </>
        )}
      </dl>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="shrink-0 opacity-70">{label}</dt>
      <dd className={cn("truncate text-right", mono && "font-mono")}>{value}</dd>
    </div>
  );
}
